<template>
  <div class="my-profile">
    <b-container v-if="!is_not_login || devBypass">
      <!-- Don't render an empty profile when the data failed to load. (TAIR3-979) -->
      <b-row v-if="!isLoading && profileLoadError">
        <b-col cols="12" md="8">
          <b-alert show variant="danger">
            {{ profileLoadError }}
            <b-button
              size="sm"
              variant="outline-danger"
              class="ml-2"
              @click="init"
              >Retry</b-button
            >
          </b-alert>
        </b-col>
      </b-row>
      <b-row v-if="!isLoading && !profileLoadError">
        <b-col cols="12" md="8">
          <b-card class="mb-4">
            <h2 class="card-title">
              Welcome, {{ personData.FIRST_NAME }} {{ personData.LAST_NAME }}
            </h2>
            <div v-if="orcidFullId" class="mb-3">
              <p class="mb-1">Your ORCID ID: {{ orcidFullId }}</p>
              <small class="text-muted">
                To unlink your ORCID account, please contact us at
                <a href="mailto:curator@arabidopsis.org"
                  >curator@arabidopsis.org</a
                >
              </small>
            </div>
            <div v-else class="mb-3">
              <p class="mb-1">No ORCID linked to your account.</p>
              <b-button
                variant="success"
                size="sm"
                :to="{ path: '/linktoorcid' }"
              >
                <img
                  src="/orcid.png"
                  alt=""
                  style="height: 18px; margin-right: 6px; vertical-align: middle;"
                />
                Connect ORCID
              </b-button>
            </div>
            <p class="font-weight-bold" v-html="subscriber_info"></p>
            <p class="font-weight-bold" v-html="subscriber_message"></p>
          </b-card>

          <usage-tracker
            v-if="isShowTracker"
            :tracker-data="trackerData"
            :bucketPurchaseLink="bucketPurchaseLink"
            :partyId="partyId"
            :onFetchTrackerData="fetchTrackerData"
            :isLoading="isTrackerDataLoading"
            class="mb-4"
          />

          <b-card class="mb-4">
            <h3 class="card-title">Contact Information</h3>
            <div class="profile-info">
              <div class="info-row" v-if="personData.EMAIL">
                <span class="info-label">Email:</span>
                <span class="info-value">{{ personData.EMAIL }}</span>
              </div>
              <div class="info-row" v-if="personData.INSTITUTIONNAME">
                <span class="info-label">Institution:</span>
                <span class="info-value">{{ personData.INSTITUTIONNAME }}</span>
              </div>
              <div class="info-row" v-if="personData.REGION">
                <span class="info-label">State/Province:</span>
                <span class="info-value">{{ personData.REGION }}</span>
              </div>
              <div class="info-row" v-if="personData.COUNTRY">
                <span class="info-label">Country/Region:</span>
                <span class="info-value">{{ personData.COUNTRY }}</span>
              </div>
              <div
                class="info-row"
                v-if="
                  !personData.EMAIL &&
                  !personData.INSTITUTIONNAME &&
                  !personData.REGION &&
                  !personData.COUNTRY
                "
              >
                <span class="info-value text-muted"
                  >No contact information found</span
                >
              </div>
            </div>
          </b-card>

          <!-- <b-card>
            <h3 class="card-title">Affiliated Organizations</h3>
            <b-list-group
              v-if="
                personData.AFFILIATIONS && personData.AFFILIATIONS.length > 0
              "
            >
              <b-list-group-item
                v-for="(o, index) in personData.AFFILIATIONS"
                :key="index"
              >
                <b-link :href="`/organization?key=${o.community_id}`">{{
                  o.name
                }}</b-link>
              </b-list-group-item>
            </b-list-group>
            <span v-else>No affiliations found</span>
          </b-card> -->
        </b-col>

        <b-col cols="12" md="4">
          <b-card class="mb-4">
            <h3 class="card-title">Quick Links</h3>
            <b-list-group>
              <b-list-group-item :href="`/person?key=${communityId}`"
                >View my public profile</b-list-group-item
              >
              <b-list-group-item :href="`/edit/person?key=${communityId}`"
                >Update Profile</b-list-group-item
              >
              <b-list-group-item
                href="https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217802/Individual+Subscription+FAQ"
                target="_blank"
                >Individual Subscription FAQ</b-list-group-item
              >
            </b-list-group>
          </b-card>

          <b-card class="mb-4">
            <h3 class="card-title">Contribute Data to TAIR</h3>
            <b-list-group>
              <b-list-group-item href="/submit/gene_class_symbol_registration/form"
                >Submit New Gene Class Symbol</b-list-group-item
              >
              <b-list-group-item href="https://goat.phoenixbioinformatics.org"
                >Online Submission for Authors and Others</b-list-group-item
              >
            </b-list-group>
          </b-card>

          <!-- Conditional rendering for TAIR Curator section -->
          <b-card v-if="personData.IS_TAIR_CURATOR === 'T'" class="mb-4">
            <h3 class="card-title">TAIR Curator Tools</h3>
            <b-list-group>
              <b-list-group-item
                href="/curator/gene_symbol_pending"
              >
                Review Gene Class Symbols
              </b-list-group-item>
              <b-list-group-item href="/browse/gene_symbols"
                >Browse Gene Class Symbols</b-list-group-item
              >
            </b-list-group>
          </b-card>
        </b-col>
      </b-row>
      <b-row v-if="isLoading">
        <b-col cols="12" md="8">Loading ...</b-col>
      </b-row>
    </b-container>

    <b-alert v-else show variant="warning"
      >You are not logged in. Please login to view this page.</b-alert
    >
  </div>
</template>

<script>
import Api from "@/services/Api";
import { mapGetters } from "vuex";
import {
  getInstitutionByIP,
  getSubscriptionStatus,
} from "@/services/SubscriptionService";
import UsageTracker from "../components/UsageTracker.vue";

export default {
  name: "Profile",
  components: {
    UsageTracker,
  },
  data() {
    return {
      trackerData: null,
      personData: {},
      is_not_login: false,
      isLoading: false,
      profileLoadError: "",
      communityId: null,
      subscriber_message: "",
      isShowTracker: false,
      isSubscribed: false,
      isSymbolRegEnabled: false,
      orcidFullId: "",
      partyId: "",
      isTrackerDataLoading: false,
      subscriber_info: "",
      devBypass: false,
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    subscribeLink() {
      return `${process.env.VUE_APP_LOGIN_BASE_URL}/#/contentaccess/subscription?partnerId=tair&redirect=${process.env.VUE_APP_UI_BASE_URL}`;
    },
    bucketPurchaseLink() {
      return `${process.env.VUE_APP_LOGIN_BASE_URL}/#/contentaccess/subscription/individual?partnerId=tair&redirect=${process.env.VUE_APP_UI_BASE_URL}&orcid_id=${this.orcidFullId}`;
    },
  },
  methods: {
    ...mapGetters({
      getCommunityId: "authProfile/getCommunityId",
      getCredentials: "authProfile/getCredentials",
      getOrcidId: "authProfile/getOrcidId",
      getPartyId: "authProfile/getPartyId",
    }),
    getAddress() {
      let address = "";
      if (this.personData.ADDRESS) {
        address += this.personData.ADDRESS + "<br />";
      }
      if (this.personData.CITY) {
        address += this.personData.CITY + ", ";
      }
      if (this.personData.REGION) {
        address += this.personData.REGION + " - ";
      }
      if (this.personData.POSTAL_CODE) {
        address += this.personData.POSTAL_CODE + "<br />";
      }
      if (this.personData.COUNTRY) {
        address += this.personData.COUNTRY;
      }

      if (address == "") {
        address = "No address found";
      }
      return address;
    },
    async init() {
      this.isLoading = true;
      this.profileLoadError = "";

      // Enable local UI-only development with ?mockProfile or localStorage flag (dev builds only)
      const isDevBuild = process.env.NODE_ENV !== "production";
      const url = new URL(window.location.href);
      const hasMockQuery = url.searchParams.has("mockProfile");
      const hasMockStorage = window.localStorage.getItem("mockProfile") === "1";
      this.devBypass = isDevBuild && (hasMockQuery || hasMockStorage);

      if (this.devBypass) {
        this.initDevMock();
        return;
      }

      let communityId = this.getCommunityId();
      if (!communityId) {
        this.is_not_login = true;
        this.isLoading = false;
        return;
      }
      this.communityId = communityId;
      let responseIP = await getInstitutionByIP();
      if (responseIP && responseIP.status) {
        if (responseIP.status == "not subscribed") {
          this.subscriber_info = `Basic TAIR Account`;
          await this.loadIndividualAccountInfo();
          this.subscriber_info += ` <br />Your institution: ${responseIP.name} is not subscribed to TAIR.`;
        } else {
          this.subscriber_info = `Institutional TAIR Account - includes full access to TAIR pages`;
          this.orcidFullId = this.getOrcidId();
          this.partyId = this.getPartyId();
          await this.fetchPersonData();
          await this.fetchTrackerData();
          await this.updateSubscriptionStatus(true);
          if (this.isSymbolRegEnabled) {
            this.subscriber_info += " and Gene Class Symbol registration.";
          } else {
            this.subscriber_info += " without Gene Class Symbol registration.";
          }
        }
      } else {
        this.subscriber_info = `Basic TAIR Account`;
        this.loadIndividualAccountInfo();
      }
      this.isLoading = false;
      this.isTrackerDataLoading = false;
    },
    initDevMock() {
      // Simulate an authenticated user and fill UI with local data
      this.is_not_login = false;
      this.communityId = "dev-community";
      this.partyId = "999999";
      this.orcidFullId = "0000-0001-2345-6789";

      this.personData = {
        FIRST_NAME: "Dev",
        LAST_NAME: "User",
        EMAIL: "dev.user@example.com",
        INSTITUTIONNAME: "Mock Institute of Botany",
        ADDRESS: "123 Research Way",
        CITY: "Science City",
        REGION: "California",
        POSTAL_CODE: "94000",
        COUNTRY: "United States",
        AFFILIATIONS: [
          { community_id: 101, name: "Mock Institute of Botany" },
          { community_id: 202, name: "Arabidopsis Lab" },
        ],
        REGISTERING_FOR_ABRC: "T",
        IS_TAIR_CURATOR: "F",
      };
      this.isSymbolRegEnabled = this.personData.REGISTERING_FOR_ABRC === "T";

      this.isShowTracker = true;
      this.trackerData = {
        total_units: 50,
        remaining_units: 17,
        expiry_date: new Date(
          Date.now() + 1000 * 60 * 60 * 24 * 45
        ).toISOString(),
        free_expiry_date: new Date(
          Date.now() + 1000 * 60 * 60 * 24 * 20
        ).toISOString(),
        first_annual_purchase_date: new Date().toISOString(),
        bucketsBought: 2,
        discountPercentage: 0,
      };

      const exp = new Date(
        Date.now() + 1000 * 60 * 60 * 24 * 45
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const mockUrl = new URL(window.location.href);
      const isInstitutional = mockUrl.searchParams.has("mockInstitutional");

      if (isInstitutional) {
        this.subscriber_info =
          "Institutional TAIR Account - includes full access to TAIR pages" +
          (this.isSymbolRegEnabled
            ? " and Gene Class Symbol registration."
            : " without Gene Class Symbol registration.");
        this.subscriber_message = `Your institutional subscription expires on ${exp}`;
      } else {
        this.subscriber_info =
          "Basic TAIR Account" +
          (this.isSymbolRegEnabled
            ? " and Gene Class Symbol registration."
            : " without Gene Class Symbol registration.");
        this.subscriber_message = "";
      }

      this.isLoading = false;
      this.isTrackerDataLoading = false;
    },
    async loadIndividualAccountInfo() {
      this.orcidFullId = this.getOrcidId();
      this.partyId = this.getPartyId();
      await this.fetchTrackerData();
      await this.fetchPersonData();
      if (this.isSymbolRegEnabled) {
        this.subscriber_info += " and Gene Class Symbol registration.";
      } else {
        this.subscriber_info += " without Gene Class Symbol registration.";
      }
    },
    async updateSubscriptionStatus(isInstitutional = false) {
      let responseSub = await getSubscriptionStatus();
      if (responseSub.subscribed) {
        this.isSubscribed = true;
        let messages = [];

        // Handle regular subscription expiry date
        if (isInstitutional && responseSub.expDate) {
          let expDate = new Date(responseSub.expDate);
          expDate = expDate.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
          messages.push(`Your institutional subscription expires on ${expDate}`);
        }

        this.subscriber_message = messages.join("<br />");
      } else {
        this.subscriber_message = "";
        this.isSubscribed = false;
      }
    },

    async fetchPersonData() {
      if (this.devBypass) return;
      try {
        // Private account data: the server requires the logged-in user's
        // credentials, sent in the body rather than the URL. (TAIR3-979)
        const { credentialId, secretKey } = this.getCredentials();
        const response = await Api().post("/user/person-details", {
          communityId: this.communityId,
          credentialId,
          secretKey,
        });
        this.personData = response.data;
        this.isSymbolRegEnabled = this.personData.REGISTERING_FOR_ABRC === "T";
      } catch (error) {
        // Log only the message: the Axios error carries the request body,
        // which includes secretKey. (TAIR3-979)
        console.error("Error fetching person data:", error.message);
        // The route now requires valid credentials: an expired session gets
        // a 401, so show the existing sign-in message. Anything else gets a
        // retryable error instead of an empty profile. (TAIR3-979)
        const status = error.response && error.response.status;
        if (status === 401) {
          this.is_not_login = true;
        } else {
          this.profileLoadError =
            "We couldn't load your profile information. Please try again.";
        }
      }
    },
    async fetchTrackerData() {
      if (this.devBypass) return;
      try {
        this.isShowTracker = true;
        this.isTrackerDataLoading = true;
        const response = await Api().get("/subscription/bucket/usage", {
          params: { party_id: this.partyId },
        });
        //keep this for local testing
        // const response = {
        //   data: {
        //     user_usage_id: 2,
        //     partyId: 297215,
        //     partner_id: "",
        //     total_units: 50,
        //     remaining_units: 3,
        //     expiry_date: "2026-03-11T22:43:57.370272Z",
        //     free_expiry_date: "2026-03-12T22:43:57.370272Z",
        //   },
        // };
        if (response.data.message) {
          this.trackerData = null;
        } else {
          this.trackerData = response.data;
        }
        this.isTrackerDataLoading = false;
      } catch (error) {
        console.error("Error fetching tracker data:", error);
        this.isTrackerDataLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.card-title {
  color: #0c335d;
  margin-bottom: 1rem;
}

.my-profile {
  background-color: #f8f9fa;
  padding: 2rem 0;
}

.profile-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.info-label {
  font-weight: 600;
  color: #495057;
  min-width: 120px;
}

.info-value {
  color: #212529;
}

@media (max-width: 768px) {
  .my-profile {
    padding: 1rem 0;
  }

  .info-row {
    flex-direction: column;
    gap: 0.25rem;
  }

  .info-label {
    min-width: auto;
  }
}
</style>
